import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Users, DollarSign, TrendingUp, Copy, LogOut, Link2, Star,
  ArrowRight, Loader2, Eye, EyeOff, UserPlus, LogIn, ChevronRight,
  BarChart3, Wallet, Clock, CheckCircle2, XCircle, Store, CreditCard, AlertCircle, ExternalLink, ShieldCheck
} from "lucide-react";
import axios from "axios";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;

function AffiliateLanding({ onStart }) {
  const steps = [
    { n: "1", title: "Cadastre-se gratis", desc: "Crie sua conta de afiliado em menos de 2 minutos. Sem custo, sem contrato." },
    { n: "2", title: "Compartilhe seu link", desc: "Receba um link exclusivo e compartilhe com restaurantes. Por WhatsApp, redes sociais ou pessoalmente." },
    { n: "3", title: "Receba automaticamente", desc: "A cada mensalidade paga pelo restaurante indicado, 20% cai direto na sua conta bancaria via Stripe." },
  ];

  const faqs = [
    { q: "Quanto vou ganhar?", a: "20% do valor da mensalidade a cada renovacao. Se o restaurante paga 79€/mes, voce recebe 15,80€/mes — para sempre, enquanto ele for cliente." },
    { q: "O pagamento e automatico?", a: "Sim, 100% automatico via Stripe. Voce configura sua conta bancaria uma unica vez e o sistema deposita automaticamente a cada pagamento do restaurante." },
    { q: "Se o restaurante parar de pagar?", a: "As comissoes cessam automaticamente. Voce so recebe enquanto o cliente estiver ativo e pagando a mensalidade." },
    { q: "Posso indicar quantos restaurantes quiser?", a: "Sim! Sem limite. Quanto mais restaurantes indicar, mais recebe. A comissao e recorrente para cada um." },
    { q: "E legal e regulamentado?", a: "Sim. O programa funciona atraves da Stripe — plataforma regulamentada globalmente. Os pagamentos sao registados e rastreados. Voce recebe um comprovativo de cada transferencia." },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Nav */}
      <div className="border-b border-gray-700/50 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
              <Star className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-sm font-bold text-white">Kaiso System <span className="text-orange-400">Afiliados</span></span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onStart} className="text-xs text-gray-400 hover:text-white transition">Ja tenho conta</button>
            <Button onClick={onStart} className="bg-orange-500 hover:bg-orange-600 text-white h-8 px-4 text-xs font-bold">
              Comecar agora
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4">
        {/* Hero */}
        <div className="text-center py-20">
          <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 text-xs text-orange-300 mb-6">
            <TrendingUp className="w-3.5 h-3.5" /> Programa de Afiliados — 100% automatico via Stripe
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-4 leading-tight">
            Ganhe <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">20%</span> para sempre<br/>
            por cada restaurante indicado
          </h1>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
            Indique restaurantes ao Kaiso System. A cada mensalidade paga, 20% e transferido automaticamente para a sua conta bancaria — recorrente, para sempre.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={onStart} className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white h-14 px-10 text-base font-bold shadow-xl shadow-orange-500/20 gap-2">
              Quero ser afiliado <ArrowRight className="w-5 h-5" />
            </Button>
            <Button onClick={onStart} variant="outline" className="border-gray-600 text-gray-300 hover:text-white hover:border-gray-400 h-14 px-8 text-sm gap-2">
              <LogIn className="w-4 h-4" /> Ja tenho conta — Entrar
            </Button>
          </div>
          {/* Social proof numbers */}
          <div className="flex justify-center gap-12 mt-14">
            {[
              { value: "20%", label: "Comissao recorrente" },
              { value: "∞", label: "Sem limite de indicacoes" },
              { value: "0€", label: "Custo para participar" },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl font-black text-white">{s.value}</p>
                <p className="text-xs text-gray-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div className="py-16 border-t border-gray-700/40">
          <h2 className="text-2xl font-black text-white text-center mb-3">Como funciona</h2>
          <p className="text-gray-400 text-sm text-center mb-10">Simples, transparente e automatico</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="relative bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center text-white font-black text-sm mb-4">{s.n}</div>
                <h3 className="text-white font-bold text-sm mb-2">{s.title}</h3>
                <p className="text-gray-400 text-xs leading-relaxed">{s.desc}</p>
                {i < steps.length - 1 && (
                  <ChevronRight className="hidden sm:block absolute -right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Example earnings */}
        <div className="py-16 border-t border-gray-700/40">
          <h2 className="text-2xl font-black text-white text-center mb-3">Quanto pode ganhar</h2>
          <p className="text-gray-400 text-sm text-center mb-10">Comissao calculada sobre a mensalidade do restaurante</p>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { plan: "Basico", price: "39€", commission: "7,80€", restaurants: "10 restaurantes", monthly: "78€/mes", color: "from-blue-500/10 to-blue-600/5 border-blue-500/20" },
              { plan: "Profissional", price: "79€", commission: "15,80€", restaurants: "10 restaurantes", monthly: "158€/mes", color: "from-orange-500/10 to-amber-600/5 border-orange-500/30", highlight: true },
              { plan: "Premium", price: "189€", commission: "37,80€", restaurants: "10 restaurantes", monthly: "378€/mes", color: "from-purple-500/10 to-purple-600/5 border-purple-500/20" },
            ].map((p, i) => (
              <div key={i} className={`bg-gradient-to-br ${p.color} border rounded-2xl p-5 ${p.highlight ? "ring-1 ring-orange-500/30" : ""}`}>
                {p.highlight && <div className="text-[10px] text-orange-400 font-bold mb-2">PLANO MAIS POPULAR</div>}
                <p className="text-xs text-gray-400 mb-1">{p.plan} ({p.price}/mes)</p>
                <p className="text-lg font-black text-white">+{p.commission} <span className="text-xs text-gray-400 font-normal">por restaurante/mes</span></p>
                <div className="mt-3 pt-3 border-t border-gray-600/30">
                  <p className="text-[10px] text-gray-500">Com {p.restaurants} ativos</p>
                  <p className="text-base font-bold text-orange-400">{p.monthly}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-gray-500 mt-4">* Valores em EUR. Brasil: calculado em R$ sobre os planos locais.</p>
        </div>

        {/* Legal & transparent */}
        <div className="py-16 border-t border-gray-700/40">
          <div className="bg-gray-800/40 border border-gray-700/50 rounded-2xl p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-base mb-1">Programa transparente e legal</h3>
                <p className="text-gray-400 text-sm">O programa de afiliados do Kaiso System e operado atraves da Stripe — plataforma de pagamentos licenciada e regulamentada na Europa (PSD2), Brasil e Estados Unidos.</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { icon: CreditCard, title: "Pagamento via Stripe", desc: "Cada transferencia e registada e rastreavel. Voce recebe comprovativo por email." },
                { icon: ShieldCheck, title: "Sem intermediarios", desc: "O dinheiro vai direto da Kaiso para a sua conta bancaria. Sem passes manuais." },
                { icon: XCircle, title: "Para quando o cliente para", desc: "As comissoes cessam automaticamente se o restaurante cancelar. Sem disputas." },
              ].map((f, i) => (
                <div key={i} className="flex gap-3">
                  <f.icon className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-white mb-1">{f.title}</p>
                    <p className="text-[11px] text-gray-500 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="py-16 border-t border-gray-700/40">
          <h2 className="text-2xl font-black text-white text-center mb-10">Perguntas frequentes</h2>
          <div className="space-y-4 max-w-2xl mx-auto">
            {faqs.map((f, i) => (
              <div key={i} className="bg-gray-800/40 border border-gray-700/50 rounded-xl px-5 py-4">
                <p className="text-sm font-bold text-white mb-1">{f.q}</p>
                <p className="text-xs text-gray-400 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="py-16 border-t border-gray-700/40 text-center">
          <h2 className="text-3xl font-black text-white mb-3">Pronto para comecar?</h2>
          <p className="text-gray-400 mb-8">Crie sua conta gratis em 2 minutos. Sem custo, sem contrato, sem burocracia.</p>
          <Button onClick={onStart} className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white h-14 px-12 text-base font-bold shadow-xl shadow-orange-500/20 gap-2">
            Criar minha conta de afiliado <ArrowRight className="w-5 h-5" />
          </Button>
          <p className="text-xs text-gray-600 mt-6">© Kaiso System. Programa de afiliados operado via Stripe Connect.</p>
        </div>
      </div>
    </div>
  );
}

function AffiliateAuth({ onLogin, onBack }) {
  const [mode, setMode] = useState("signup"); // signup | login
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", pix_or_iban: "" });
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        if (!form.name || !form.email || !form.phone || !form.password) {
          toast.error("Preencha todos os campos obrigatorios");
          setLoading(false); return;
        }
        const res = await axios.post(`${API}/api/v1/affiliate/signup`, form);
        localStorage.setItem("affiliate_token", res.data.access_token);
        toast.success("Conta criada! Bem-vindo ao programa de afiliados.");
        onLogin(res.data);
      } else {
        const res = await axios.post(`${API}/api/v1/affiliate/login`, { email: form.email, password: form.password });
        localStorage.setItem("affiliate_token", res.data.access_token);
        toast.success("Login realizado!");
        onLogin(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao processar");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-orange-500/20">
            <Star className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Programa de Afiliados</h1>
          <p className="text-sm text-gray-400">Kaiso System</p>
        </div>

        {/* Mode Tabs */}
        <div className="flex bg-gray-800/50 rounded-xl p-1 mb-6 border border-gray-700/50">
          <button
            onClick={() => setMode("signup")}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${mode === "signup" ? "bg-orange-500 text-white shadow-lg" : "text-gray-400 hover:text-white"}`}
            data-testid="affiliate-tab-signup"
          >
            <UserPlus className="w-4 h-4" /> Cadastrar
          </button>
          <button
            onClick={() => setMode("login")}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${mode === "login" ? "bg-orange-500 text-white shadow-lg" : "text-gray-400 hover:text-white"}`}
            data-testid="affiliate-tab-login"
          >
            <LogIn className="w-4 h-4" /> Entrar
          </button>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="space-y-4">
          <Card className="bg-gray-800/50 border-gray-700/50 backdrop-blur">
            <CardContent className="p-5 space-y-4">
              {mode === "signup" && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-gray-300 text-xs">Nome completo *</Label>
                    <Input value={form.name} onChange={e => u("name", e.target.value)} placeholder="Joao Silva" className="bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500 h-11" data-testid="affiliate-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-gray-300 text-xs">Telefone / WhatsApp *</Label>
                    <Input value={form.phone} onChange={e => u("phone", e.target.value)} placeholder="+34 600 000 000" className="bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500 h-11" data-testid="affiliate-phone" />
                  </div>
                </>
              )}
              <div className="space-y-1.5">
                <Label className="text-gray-300 text-xs">Email *</Label>
                <Input type="email" value={form.email} onChange={e => u("email", e.target.value)} placeholder="seu@email.com" className="bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500 h-11" data-testid="affiliate-email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-300 text-xs">Senha *</Label>
                <div className="relative">
                  <Input type={showPw ? "text" : "password"} value={form.password} onChange={e => u("password", e.target.value)} placeholder="Min 6 caracteres" className="bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500 h-11 pr-10" data-testid="affiliate-password" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {mode === "signup" && (
                <div className="space-y-1.5">
                  <Label className="text-gray-300 text-xs">PIX / IBAN (para receber comissoes)</Label>
                  <Input value={form.pix_or_iban} onChange={e => u("pix_or_iban", e.target.value)} placeholder="ES12 3456 7890 1234 5678 9012" className="bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500 h-11" data-testid="affiliate-iban" />
                  <p className="text-[10px] text-gray-500">Pode preencher depois no perfil</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Button type="submit" disabled={loading} className="w-full h-12 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-bold text-sm shadow-lg shadow-orange-500/20" data-testid="affiliate-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {mode === "signup" ? "Criar Minha Conta" : "Entrar"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </form>

        {mode === "signup" && (
          <div className="mt-6 grid grid-cols-3 gap-3">
            {[
              { icon: Link2, label: "Link unico", desc: "Seu link personalizado" },
              { icon: DollarSign, label: "20% para sempre", desc: "Pagamento automatico" },
              { icon: TrendingUp, label: "Recorrente", desc: "Todos os meses" },
            ].map((b, i) => (
              <div key={i} className="bg-gray-800/30 border border-gray-700/30 rounded-xl p-3 text-center">
                <b.icon className="w-5 h-5 text-orange-400 mx-auto mb-1" />
                <p className="text-[10px] font-bold text-white">{b.label}</p>
                <p className="text-[9px] text-gray-500">{b.desc}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-center gap-4 mt-6 text-xs text-gray-500">
          {onBack && (
            <button onClick={onBack} className="hover:text-orange-400 transition">← Voltar</button>
          )}
          <a href="/" className="hover:text-orange-400 transition">Voltar ao site</a>
        </div>
      </div>
    </div>
  );
}

function AffiliateDashboard({ initialData }) {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(!initialData);
  const [tab, setTab] = useState("overview");
  const [connectStatus, setConnectStatus] = useState(null);

  const fetchDashboard = useCallback(async () => {
    const token = localStorage.getItem("affiliate_token");
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/v1/affiliate/dashboard?token=${token}`);
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("affiliate_token");
        window.location.reload();
      }
      toast.error("Erro ao carregar dados");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!initialData) fetchDashboard();
  }, [initialData, fetchDashboard]);

  useEffect(() => {
    const token = localStorage.getItem("affiliate_token");
    if (token) {
      axios.get(`${API}/api/v1/affiliate/connect/status?token=${token}`)
        .then(r => setConnectStatus(r.data))
        .catch(() => setConnectStatus({ connected: false, onboarding_complete: false }));
    }
  }, []);

  const logout = () => {
    localStorage.removeItem("affiliate_token");
    window.location.reload();
  };

  const copyLink = () => {
    const link = `${window.location.origin}/signup?ref=${data?.affiliate?.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copiado!");
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  const { affiliate, stats, referrals } = data;
  const refLink = `${window.location.origin}/signup?ref=${affiliate.referral_code}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Top Bar */}
      <div className="bg-gray-800/80 border-b border-gray-700/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
              <Star className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-white">Kaiso System</h1>
              <p className="text-[10px] text-gray-400">Painel do Afiliado</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">{affiliate.name}</span>
            <button onClick={logout} className="text-gray-500 hover:text-red-400 transition" data-testid="affiliate-logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Referral Link Card */}
        <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/20 rounded-2xl p-5 mb-6" data-testid="affiliate-link-card">
          <div className="flex items-center gap-2 mb-3">
            <Link2 className="w-5 h-5 text-orange-400" />
            <h2 className="text-sm font-bold text-white">Seu Link de Indicacao</h2>
            <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/30 text-[10px]">{affiliate.referral_code}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-800/80 rounded-xl px-4 py-2.5 font-mono text-xs text-orange-300 truncate border border-gray-700/50" data-testid="affiliate-link-text">
              {refLink}
            </div>
            <Button onClick={copyLink} className="bg-orange-500 hover:bg-orange-600 text-white gap-1.5 flex-shrink-0" data-testid="affiliate-copy-link">
              <Copy className="w-4 h-4" /> Copiar
            </Button>
          </div>
          <p className="text-[10px] text-gray-500 mt-2">
            Compartilhe esse link. Quando um restaurante se cadastrar por ele, voce ganha{" "}
            {stats.commission_type === "fixed"
              ? `${stats.commission_value} EUR`
              : `${stats.commission_value}%`
            } de comissao {stats.commission_type === "fixed" ? "por indicacao" : "recorrente a cada mensalidade"}, automaticamente via Stripe.
          </p>
        </div>

        {/* Stripe Connect warning banner */}
        {connectStatus !== null && !connectStatus.onboarding_complete && (
          <div
            className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-2 cursor-pointer hover:bg-amber-500/15 transition"
            onClick={() => setTab("profile")}
            data-testid="affiliate-connect-warning"
          >
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-amber-300">Conta bancaria nao configurada</p>
              <p className="text-[10px] text-amber-400/80">Configure sua conta no Stripe para receber os 20% automaticamente. Clique aqui.</p>
            </div>
            <ChevronRight className="w-4 h-4 text-amber-400 flex-shrink-0" />
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { icon: Users, label: "Indicacoes", value: stats.total_referrals, color: "text-blue-400", bg: "bg-blue-500/10" },
            { icon: CheckCircle2, label: "Ativos", value: stats.active_referrals, color: "text-emerald-400", bg: "bg-emerald-500/10" },
            { icon: DollarSign, label: "Total Ganho", value: `${stats.total_earned.toFixed(2)} EUR`, color: "text-orange-400", bg: "bg-orange-500/10" },
            { icon: Wallet, label: "A Receber", value: `${stats.pending_amount.toFixed(2)} EUR`, color: "text-amber-400", bg: "bg-amber-500/10" },
          ].map((s, i) => (
            <Card key={i} className="bg-gray-800/50 border-gray-700/50" data-testid={`affiliate-stat-${i}`}>
              <CardContent className="p-4">
                <div className={`w-9 h-9 rounded-xl ${s.bg} flex items-center justify-center mb-2`}>
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                </div>
                <p className="text-lg font-bold text-white">{s.value}</p>
                <p className="text-[10px] text-gray-500">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-800/50 rounded-xl p-1 mb-4 border border-gray-700/50">
          {[
            { id: "overview", label: "Indicacoes", icon: Store },
            { id: "commissions", label: "Comissoes", icon: BarChart3 },
            { id: "profile", label: "Perfil", icon: Users },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${tab === t.id ? "bg-gray-700 text-white" : "text-gray-500 hover:text-gray-300"}`}
              data-testid={`affiliate-tab-${t.id}`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {tab === "overview" && (
          <div className="space-y-2">
            {referrals.length === 0 ? (
              <Card className="bg-gray-800/50 border-gray-700/50">
                <CardContent className="p-8 text-center">
                  <Users className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <p className="text-sm text-gray-400 font-medium">Nenhuma indicacao ainda</p>
                  <p className="text-xs text-gray-500 mt-1">Compartilhe seu link e comece a ganhar comissoes!</p>
                </CardContent>
              </Card>
            ) : (
              referrals.map((r, i) => (
                <Card key={i} className="bg-gray-800/50 border-gray-700/50" data-testid={`affiliate-referral-${i}`}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-700 rounded-xl flex items-center justify-center">
                      <Store className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{r.restaurant_name || "Restaurante"}</p>
                      <p className="text-[10px] text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleDateString("pt-BR") : ""}</p>
                    </div>
                    <Badge className={`text-[10px] ${r.status === "active" ? "bg-emerald-500/20 text-emerald-300" : r.status === "trial" ? "bg-blue-500/20 text-blue-300" : "bg-red-500/20 text-red-300"}`}>
                      {r.status === "active" ? "Ativo" : r.status === "trial" ? "Trial" : "Cancelado"}
                    </Badge>
                    <div className="text-right">
                      <p className="text-sm font-bold text-orange-400">{r.commission_earned.toFixed(2)} EUR</p>
                      <p className="text-[10px] text-gray-500">comissao</p>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {tab === "commissions" && (
          <div className="space-y-2">
            {(data.commissions || []).length === 0 ? (
              <Card className="bg-gray-800/50 border-gray-700/50">
                <CardContent className="p-8 text-center">
                  <BarChart3 className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <p className="text-sm text-gray-400 font-medium">Nenhuma comissao registrada</p>
                  <p className="text-xs text-gray-500 mt-1">Comissoes aparecem quando restaurantes indicados pagam mensalidade</p>
                </CardContent>
              </Card>
            ) : (
              data.commissions.map((c, i) => (
                <Card key={i} className="bg-gray-800/50 border-gray-700/50">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${c.paid ? "bg-emerald-500/20" : "bg-amber-500/20"}`}>
                      {c.paid ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Clock className="w-4 h-4 text-amber-400" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-white">{c.restaurant_name || "Restaurante"}</p>
                      <p className="text-[10px] text-gray-500">{c.created_at ? new Date(c.created_at).toLocaleDateString("pt-BR") : ""}</p>
                    </div>
                    <Badge className={c.paid ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}>
                      {c.paid ? "Pago" : "Pendente"}
                    </Badge>
                    <p className="text-sm font-bold text-white">{(c.amount || 0).toFixed(2)} EUR</p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {tab === "profile" && (
          <AffiliateProfile affiliate={affiliate} onUpdate={fetchDashboard} />
        )}
      </div>
    </div>
  );
}

function AffiliateProfile({ affiliate, onUpdate }) {
  const [form, setForm] = useState({ name: affiliate.name, phone: affiliate.phone });
  const [saving, setSaving] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("affiliate_token");
    axios.get(`${API}/api/v1/affiliate/connect/status?token=${token}`)
      .then(r => setConnectStatus(r.data))
      .catch(() => setConnectStatus({ connected: false, onboarding_complete: false }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("affiliate_token");
      await axios.patch(`${API}/api/v1/affiliate/profile?token=${token}`, form);
      toast.success("Perfil atualizado!");
      onUpdate();
    } catch {
      toast.error("Erro ao salvar");
    }
    setSaving(false);
  };

  const startConnect = async () => {
    setConnectLoading(true);
    try {
      const token = localStorage.getItem("affiliate_token");
      const returnUrl = window.location.origin + "/affiliate";
      const res = await axios.get(`${API}/api/v1/affiliate/connect/onboarding?token=${token}&return_url=${encodeURIComponent(returnUrl)}`);
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao iniciar configuracao bancaria");
      setConnectLoading(false);
    }
  };

  const isConnected = connectStatus?.onboarding_complete;

  return (
    <div className="space-y-4">
      {/* Stripe Connect - Conta bancaria (OBRIGATORIO) */}
      <Card className={`border-2 ${isConnected ? "bg-emerald-900/20 border-emerald-500/40" : "bg-amber-900/20 border-amber-500/40"}`}>
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isConnected ? "bg-emerald-500/20" : "bg-amber-500/20"}`}>
              {isConnected ? <ShieldCheck className="w-5 h-5 text-emerald-400" /> : <AlertCircle className="w-5 h-5 text-amber-400" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-bold text-white">Conta Bancaria para Recebimento</h3>
                <Badge className={isConnected ? "bg-emerald-500/20 text-emerald-300 border-0 text-[10px]" : "bg-amber-500/20 text-amber-300 border-0 text-[10px]"}>
                  {isConnected ? "Verificada" : "Obrigatorio"}
                </Badge>
              </div>
              {isConnected ? (
                <p className="text-xs text-emerald-300">Conta configurada. As comissoes de 20% sao transferidas automaticamente via Stripe quando os restaurantes pagam.</p>
              ) : (
                <p className="text-xs text-amber-300">Voce precisa configurar sua conta bancaria para receber os 20% de comissao automaticamente. Sem isso, as comissoes ficam pendentes.</p>
              )}
            </div>
          </div>
          {!isConnected && (
            <Button
              onClick={startConnect}
              disabled={connectLoading || connectStatus === null}
              className="w-full mt-4 bg-amber-500 hover:bg-amber-600 text-white font-bold h-11 gap-2"
              data-testid="affiliate-connect-button"
            >
              {connectLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              {connectLoading ? "Redirecionando..." : "Configurar Conta Bancaria"}
              {!connectLoading && <ExternalLink className="w-3.5 h-3.5 opacity-60" />}
            </Button>
          )}
          {isConnected && (
            <Button
              onClick={startConnect}
              disabled={connectLoading}
              variant="outline"
              className="w-full mt-4 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 h-9 gap-2 text-xs"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Gerenciar conta no Stripe
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Dados do perfil */}
      <Card className="bg-gray-800/50 border-gray-700/50">
        <CardContent className="p-5 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-gray-300 text-xs">Nome</Label>
            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="bg-gray-700/50 border-gray-600 text-white h-11" data-testid="affiliate-profile-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-gray-300 text-xs">Email</Label>
            <Input value={affiliate.email} disabled className="bg-gray-700/30 border-gray-600 text-gray-400 h-11" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-gray-300 text-xs">Telefone</Label>
            <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="bg-gray-700/50 border-gray-600 text-white h-11" data-testid="affiliate-profile-phone" />
          </div>
          <div className="bg-gray-700/30 rounded-lg p-3 text-xs text-gray-400">
            <p>Codigo de referencia: <span className="font-mono text-orange-400">{affiliate.referral_code}</span></p>
            <p>Membro desde: {affiliate.created_at ? new Date(affiliate.created_at).toLocaleDateString("pt-BR") : "N/A"}</p>
          </div>
          <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white w-full" data-testid="affiliate-profile-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Salvar Alteracoes
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default function AffiliatePage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [dashData, setDashData] = useState(null);
  const [checking, setChecking] = useState(true);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    // If returning from Stripe Connect
    const params = new URLSearchParams(window.location.search);
    if (params.get("connect") === "success" || params.get("connect") === "refresh") {
      const token = localStorage.getItem("affiliate_token");
      if (token) {
        // Force reload dashboard to update connect status
        axios.get(`${API}/api/v1/affiliate/dashboard?token=${token}`)
          .then(res => { setDashData(res.data); setAuthenticated(true); })
          .catch(() => localStorage.removeItem("affiliate_token"))
          .finally(() => setChecking(false));
        return;
      }
    }

    const token = localStorage.getItem("affiliate_token");
    if (token) {
      axios.get(`${API}/api/v1/affiliate/dashboard?token=${token}`)
        .then(res => {
          setDashData(res.data);
          setAuthenticated(true);
        })
        .catch(() => {
          localStorage.removeItem("affiliate_token");
        })
        .finally(() => setChecking(false));
    } else {
      setChecking(false);
    }
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (authenticated) {
    return <AffiliateDashboard initialData={dashData} />;
  }

  if (showAuth) {
    return (
      <AffiliateAuth onLogin={(data) => {
        setDashData({ affiliate: data.affiliate, stats: { total_referrals: 0, active_referrals: 0, total_earned: 0, total_paid: 0, pending_amount: 0, commission_type: data.affiliate?.commission_type || "percentage", commission_value: data.affiliate?.commission_value || 20 }, referrals: [], commissions: [] });
        setAuthenticated(true);
      }} onBack={() => setShowAuth(false)} />
    );
  }

  return <AffiliateLanding onStart={() => setShowAuth(true)} />;
}
